import { MAIL_PROFILES, MailTemplate, resolveMailProfile } from "@chaimera/core";
import { ChaimeraEditor } from "@chaimera/editor";
import { campaignMailMatch } from "@shared/auspost/match.ts";
import { unaddressedProofTemplate } from "@shared/print-proof.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { DesignOpenGate, ProofEditWarning } from "../components/DesignEditPrompt.tsx";
import { useAppDm } from "../components/legacy/app-state.tsx";
import { LoadingSpinnerWithBackground, useToast } from "../components/legacy/ui.tsx";
import { NameDesignModal } from "../components/NameDesignModal.tsx";
import { api } from "../lib/api.ts";
import { adoptExport, builderClient, listDesigns } from "../lib/artwork-builder.ts";
import { specAudience, useActiveAusPostSpec } from "../lib/auspost-spec.ts";

/**
 * DIY Design Tool: the Chaimera editor, full screen over the workspace.
 * Under /app/$customerId so AppDmProvider loads the campaign from $campaignId.
 * Export PDF adopts the file into the artwork library and selects it for the campaign.
 */
export const Route = createFileRoute("/app/$customerId/design/$campaignId/$designId")({
  component: DesignTool,
  validateSearch: (raw: Record<string, unknown>): { scope?: "ready" } =>
    raw.scope === "ready" ? { scope: "ready" } : {},
});

function DesignTool() {
  const { customerId, campaignId, designId } = Route.useParams();
  const { scope } = Route.useSearch();
  const dm = useAppDm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const owner = dm.campaign ? (dm.campaign.customer_id ?? dm.campaign.user_id) : null;
  const client = useMemo(() => (owner ? builderClient(owner) : null), [owner]);
  const designs = useQuery({
    queryKey: ["designs", owner],
    enabled: Boolean(client),
    queryFn: () => listDesigns(owner!),
  });
  const known = designs.data?.find((d) => d.id === designId);
  const nameIt = useMutation({
    mutationFn: (name: string) => client!.renameArtwork(designId, name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["designs", owner] }),
    onError: (e: Error) => showToast(e.message || "Could not name the design", "error"),
  });
  const addressed = dm.campaign?.mail_type === "addressed";
  const unaddressed = dm.campaign?.mail_type === "unaddressed";
  const productId = addressed ? dm.campaign?.product_id : null;
  const template = useQuery({
    queryKey: ["mail-template", productId],
    enabled: Boolean(productId),
    queryFn: () => api.get<{ template: unknown }>(`/catalog/templates/${productId}`),
  });
  const parsed = template.data?.template ? MailTemplate.safeParse(template.data.template) : null;
  const product = dm.products.find((item) => item.id === dm.campaign?.product_id);
  const flyer =
    unaddressed && product
      ? unaddressedProofTemplate(product.width_mm, product.height_mm, product.pages)
      : null;
  const mailGuides = flyer
    ? { template: flyer, page: flyer.panelPage }
    : parsed?.success && dm.campaign
      ? { template: parsed.data, page: parsed.data.panelPage }
      : undefined;
  const match =
    dm.campaign && product
      ? campaignMailMatch({
          slug: product.slug,
          mailType: dm.campaign.mail_type,
          mailSpeed: dm.campaign.mail_speed,
          widthMm: product.width_mm,
          heightMm: product.height_mm,
          templatePresentation: parsed?.success ? parsed.data.presentation : null,
        })
      : null;
  const ausPost = useActiveAusPostSpec(match);
  const profile =
    match && ausPost.data
      ? resolveMailProfile(match, MAIL_PROFILES, ausPost.data.spec.epsilon)
      : null;
  const audience = specAudience(dm.user?.role);

  const back = () => {
    queryClient.invalidateQueries({ queryKey: ["designs"] });
    queryClient.invalidateQueries({ queryKey: ["final-pdf", campaignId] });
    dm.set({ appContext: "manageArtwork", showAddressList: false });
    navigate({
      to: "/app/$customerId/mycampaigns/$campaignId",
      params: { customerId, campaignId },
    });
  };

  if (!client || !designs.data || (designs.isFetching && !known) || ausPost.isPending) {
    return <LoadingSpinnerWithBackground />;
  }
  if (ausPost.isError || !ausPost.data) {
    return <p className="p-6 text-sm text-gray-700">AusPost spec is not loaded.</p>;
  }
  if (known?.name === "") {
    return (
      <NameDesignModal
        pending={nameIt.isPending}
        onCancel={back}
        onSave={(name) => nameIt.mutate(name)}
      />
    );
  }
  return (
    <DesignOpenGate
      designId={designId}
      campaignId={campaignId}
      customerId={customerId}
      scope={scope}
      onLeave={back}
    >
      {({ warnProof, onSaved }) => (
        <div className="fixed inset-0 z-50 flex flex-col bg-white">
          <ProofEditWarning show={warnProof} />
          <div className="min-h-0 flex-1">
            <ChaimeraEditor
              className="h-full"
              client={client}
              artworkId={designId}
              mailGuides={mailGuides}
              mailProfileId={profile?.id ?? null}
              ausPostSpec={ausPost.data.spec}
              mailType={unaddressed ? "unaddressed" : addressed ? "addressed" : null}
              mailPriority={addressed && dm.campaign?.mail_speed === "priority"}
              specRules={
                profile
                  ? ausPost.data.lines
                  : ausPost.data.lines.map((line) => ({ ...line, strictness: "info" }))
              }
              specAudience={audience}
              specAdminHref={audience === "admin" ? `/app/${customerId}/auspost-specs` : null}
              logoSrc="/logo-96-96.png"
              onBack={back}
              onSaved={onSaved}
              onExported={async (rec) => {
                // PNG/JPG are proofs for the user; only the PDF is print artwork.
                if (rec.format !== "pdf") return undefined;
                try {
                  const artwork = await adoptExport(rec.id, campaignId);
                  await dm.updateCampaign({ artworkId: artwork.id, designId });
                  queryClient.invalidateQueries({ queryKey: ["artwork"] });
                  showToast("Design added to campaign", "success");
                  back();
                } catch (e) {
                  showToast((e as Error).message || "Could not add the design", "error");
                }
                return false;
              }}
            />
          </div>
        </div>
      )}
    </DesignOpenGate>
  );
}
